/**
 * REACTIVE STORE - The Idioma Club State Management
 * 
 * A lightweight reactive store inspired by Zustand/Redux.
 * Features:
 * - Reactive subscriptions (UI updates automatically)
 * - Persistence (localStorage sync)
 * - DevTools support (console logging in dev)
 * - Offline-first ready
 * 
 * Refactored to use a class for proper TypeScript inference.
 */

import { registry } from './registry.ts';
import { RankingEntry } from '../domain/entities/Ranking';
import { ClanData, ClanMember, ClanActivity } from '../domain/entities/Clan';
import { User } from '../domain/entities/User';

// Define proper types
export type UserProfile = User;



export interface AppState {
    user: UserProfile | null;
    isAuthenticated: boolean;
    totalXp: number;
    levelTitle: string;
    levelProgress: number;
    currentStreak: number;
    lives: number;
    gems: number;
    currentWorld: string | null;
    currentLanguage: string | null;
    currentNode: string | null;
    completedLessons: string[];
    completedLessonsCount: number;
    rankings: RankingEntry[];
    clan: ClanData;
    clanMembers: ClanMember[];
    clanActivities: ClanActivity[];
    currentView: string;
    isLoading: boolean;
    lastSync: string | null;
    mapTotalLessons: number;
    mapCompletedLessons: number;
    mapCompletionPercent: number;
    [key: string]: unknown;
}

type StoreSubscriber = (value: unknown, key: string) => void;
type GlobalSubscriber = (state: AppState, changedKeys: string[]) => void;

interface ComputedValue {
    fn: () => unknown;
    deps: string[];
    cache: unknown;
    dirty: boolean;
}

// Global declaration
declare global {
    interface Window {
        ReactiveStore?: ReactiveStore;
        Store?: ReactiveStore;
        // registry is declared in global.d.ts
    }
}

const DEFAULT_CLAN: ClanData = {
    name: '...',
    level: 0,
    totalXp: 0,
    members: 0,
    rank: 0,
    weeklyGoal: 0,
    weeklyProgress: 0,
    teacher: { name: 'Sin asignar', avatar: '' }
};

const DEFAULT_STATE: AppState = {
    user: null,
    isAuthenticated: false,
    totalXp: 0,
    levelTitle: 'Starter',
    levelProgress: 0,
    currentStreak: 0,
    lives: 5,
    gems: 100,
    currentWorld: null,
    currentLanguage: 'en',
    currentNode: null,
    completedLessons: [],
    completedLessonsCount: 0,
    rankings: [],
    clan: { ...DEFAULT_CLAN },
    clanMembers: [],
    clanActivities: [],
    currentView: 'map',
    isLoading: false,
    lastSync: null,
    mapTotalLessons: 0,
    mapCompletedLessons: 0,
    mapCompletionPercent: 0
};

// Type alias for backwards compatibility
export type IReactiveStore = ReactiveStore;

export class ReactiveStore {
    private _state: AppState;
    private _subscribers: Map<string, Set<StoreSubscriber>>;
    private _globalSubscribers: Set<GlobalSubscriber>;
    private _batchDepth: number = 0;
    private _batchedUpdates: Partial<AppState> = {};
    private _persistTimer: ReturnType<typeof setTimeout> | null = null;
    private _rafId: number | null = null;
    private _pendingNotifications: Map<string, unknown>;
    private _computed: Map<string, ComputedValue>;

    constructor() {
        this._state = { ...DEFAULT_STATE };
        this._subscribers = new Map();
        this._globalSubscribers = new Set();
        this._pendingNotifications = new Map();
        this._computed = new Map();
    }

    // === PUBLIC API ===

    get<K extends keyof AppState>(key?: K): K extends undefined ? AppState : AppState[K] {
        if (key) return this._state[key] as AppState[K] & (K extends undefined ? AppState : AppState[K]);
        return { ...this._state } as AppState[K] & (K extends undefined ? AppState : AppState[K]);
    }

    set(updates: Partial<AppState>): void {
        if (this._batchDepth > 0) {
            Object.assign(this._batchedUpdates, updates);
            return;
        }

        const changedKeys: string[] = [];

        for (const [key, value] of Object.entries(updates)) {
            if (this._state[key] !== value) {
                this._state[key] = value;
                changedKeys.push(key);

                // Invalidate computed values that depend on this key
                this._computed.forEach((computed) => {
                    if (computed.deps.includes(key)) {
                        computed.dirty = true;
                    }
                });
            }
        }

        // Schedule notifications via RAF for smooth rendering
        for (const key of changedKeys) {
            this._scheduleNotify(key, this._state[key]);
        }

        // Notify global subscribers immediately
        if (changedKeys.length > 0) {
            this._globalSubscribers.forEach(callback => {
                try {
                    callback(this._state, changedKeys);
                } catch (e) {
                    console.error('[Store] Global subscriber error:', e);
                }
            });

            this._schedulePersist();

            if (import.meta.env?.DEV) {
                console.log('📦 Store Update:', changedKeys, updates);
            }
        }
    }

    // === BATCHING API ===

    batch(fn: () => void): void {
        this._batchDepth++;
        try {
            fn();
        } finally {
            this._batchDepth--;
            if (this._batchDepth === 0) {
                this._flushBatch();
            }
        }
    }

    private _flushBatch(): void {
        if (Object.keys(this._batchedUpdates).length > 0) {
            const updates = this._batchedUpdates;
            this._batchedUpdates = {};
            this.set(updates);
        }
    }

    // === PERFORMANCE HELPERS ===

    public persist(): void {
        if (this._persistTimer) clearTimeout(this._persistTimer);
        this._persist();
        this._persistTimer = null;
    }

    private _schedulePersist(): void {
        if (this._persistTimer) clearTimeout(this._persistTimer);
        this._persistTimer = setTimeout(() => {
            this.persist();
        }, 300);
    }

    private _scheduleNotify(key: string, value: unknown): void {
        this._pendingNotifications.set(key, value);

        if (!this._rafId) {
            this._rafId = requestAnimationFrame(() => {
                this._flushNotifications();
            });
        }
    }

    private _flushNotifications(): void {
        const notifications = new Map(this._pendingNotifications);
        this._pendingNotifications.clear();
        this._rafId = null;

        notifications.forEach((value, key) => {
            const subs = this._subscribers.get(key);
            if (subs) {
                subs.forEach((callback) => {
                    try {
                        callback(value, key);
                    } catch (e) {
                        console.error(`[Store] Subscriber error for ${key}:`, e);
                    }
                });
            }
        });
    }

    // === COMPUTED VALUES ===

    compute<T>(name: string, fn: () => T, deps: string[]): T {
        const cached = this._computed.get(name);

        if (cached && !cached.dirty) {
            return cached.cache as T;
        }

        const result = fn();
        this._computed.set(name, {
            fn,
            deps,
            cache: result,
            dirty: false
        });

        return result;
    }

    subscribe(key: string, callback: StoreSubscriber): () => void {
        if (typeof callback !== 'function') {
            console.error('[Store] subscribe called without a function callback for key:', key, 'callback:', callback);
            return () => { };
        }

        let subs = this._subscribers.get(key);
        if (!subs) { subs = new Set(); this._subscribers.set(key, subs); }
        subs.add(callback);

        // Immediately call with current value
        callback(this._state[key], key);

        // Return unsubscribe function
        return () => this._subscribers.get(key)?.delete(callback);
    }

    subscribeAll(callback: GlobalSubscriber): () => void {
        this._globalSubscribers.add(callback);
        return () => this._globalSubscribers.delete(callback);
    }

    // === PERSISTENCE ===

    private _persist(): void {
        try {
            // totalXp is NOT persisted — DB (profiles.total_xp) is the SSOT.
            // On every login, auth.ts reads the real value from the DB.
            const toSave = {
                currentStreak: this._state.currentStreak,
                lives: this._state.lives,
                gems: this._state.gems,
                currentWorld: this._state.currentWorld,
                currentLanguage: this._state.currentLanguage,
                currentNode: this._state.currentNode,
                completedLessons: this._state.completedLessons,
                completedLessonsCount: this._state.completedLessonsCount,
                lastSync: new Date().toISOString()
            };
            localStorage.setItem('idiomaclub_store', JSON.stringify(toSave));
        } catch (e) {
            console.warn('[Store] Persistence failed:', e);
        }
    }

    private _hydrate(): void {
        try {
            const saved = localStorage.getItem('idiomaclub_store');
            if (saved) {
                const data = JSON.parse(saved);
                
                // 🛡️ Data Sanitization Guard:
                // Prevents legacy object values from crashing UUID endpoints (e.g. courseId=[object Object])
                if (data && typeof data.currentWorld === 'object' && data.currentWorld !== null) {
                    // Try to safely extract string ID if possible, otherwise nullify
                    data.currentWorld = data.currentWorld.id ?? data.currentWorld.courseId ?? null;
                    if (typeof data.currentWorld !== 'string') data.currentWorld = null;
                }

                this._state = { ...this._state, ...data };
                console.log('💧 Store hydrated from localStorage');
            }
        } catch (e) {
            console.warn('[Store] Hydration failed:', e);
        }
    }

    // === ACTIONS (Business Logic) ===

    readonly actions = {
        setUser: (user: UserProfile | null) => {
            const currentStreak = this.get('currentStreak') || 0;

            // DB is SSOT for XP — always use the value from profiles.total_xp
            const newUserXp = user?.totalXp ?? 0;
            const newUserStreak = user?.streak ?? 0;

            // ⚡ PERF: Skip if nothing actually changed (prevents double re-render cascade at boot)
            const resolvedXp = newUserXp; // No Math.max — DB wins always
            const resolvedStreak = Math.max(currentStreak, newUserStreak);
            const resolvedLives = user?.lives ?? this.get('lives') ?? 5;
            const resolvedGems = user?.gems ?? this.get('gems') ?? 100;

            const currentUser = this.get('user');
            if (
                currentUser?.id === user?.id &&
                this.get('totalXp') === resolvedXp &&
                this.get('currentStreak') === resolvedStreak &&
                this.get('lives') === resolvedLives &&
                this.get('gems') === resolvedGems
            ) {
                if (import.meta.env?.DEV) {
                    console.log(`👤 [Store] setUser: SKIPPED (no change)`);
                }
                return;
            }

            if (import.meta.env?.DEV) {
                console.log(`👤 [Store] setUser: DB XP=${newUserXp} (SSOT: profiles.total_xp)`);
            }

            this.set({
                user,
                isAuthenticated: !!user,
                totalXp: resolvedXp,
                currentStreak: resolvedStreak,
                lives: resolvedLives,
                gems: resolvedGems
            });
        },

        setRankings: (rankings: RankingEntry[]) => {
            this.set({ rankings });
        },

        addXp: (amount: number) => {
            const current = this.get('totalXp') || 0;
            this.set({ totalXp: current + amount });
        },

        completeLesson: (lessonId: string) => {
            const completed = [...(this.get('completedLessons') || [])];
            if (!completed.includes(lessonId)) {
                completed.push(lessonId);
                this.set({
                    completedLessons: completed,
                    completedLessonsCount: completed.length
                });
            }
        },

        setCurrentPosition: (worldId: string, nodeId: string, language?: string) => {
            this.set({
                currentWorld: worldId,
                currentLanguage: language ?? this.get('currentLanguage'),
                currentNode: nodeId
            });
        },

        setView: (viewName: string) => {
            this.set({ currentView: viewName });
        },

        /** Decrement lives by 1 (min 0) + emit life_lost event to DB */
        decrementLife: () => {
            const current = this.get('lives') ?? 5;
            const newLives = Math.max(current - 1, 0);
            this.set({ lives: newLives });

            // Fire-and-forget: persist to DB via user_events
            const userId = this._state.user?.id;
            if (!userId) return;
            void (async () => {
                try {
                    const { getSupabaseClient } = await import('./supabase');
                    const client = getSupabaseClient();
                    if (!client) return;
                    await client.from('user_events').insert({
                        user_id: userId,
                        event_type: 'life_lost',
                        event_data: { remaining: newLives },
                    });
                } catch { /* silent */ }
            })();
        },

        /** Refill lives to max (5) — costs gems */
        refillLives: (gemCost: number = 50) => {
            const currentGems = this.get('gems') ?? 0;
            if (currentGems < gemCost) return false;
            this.set({ lives: 5, gems: currentGems - gemCost });

            // Fire-and-forget: persist gem transaction + refill
            const userId = this._state.user?.id;
            if (userId) {
                void (async () => {
                    try {
                        const { getSupabaseClient } = await import('./supabase');
                        const client = getSupabaseClient();
                        if (!client) return;
                        await client.from('user_events').insert({
                            user_id: userId,
                            event_type: 'gem_transaction',
                            event_data: { amount: -gemCost, purpose: 'refill_lives' },
                        });
                    } catch { /* silent */ }
                })();
            }
            return true;
        },

        reset: () => {
            localStorage.removeItem('idiomaclub_store');
            this._state = { ...DEFAULT_STATE };
            this._globalSubscribers.forEach(cb => cb(this._state, ['reset']));
        }
    };

    init(): void {
        this._hydrate();
        console.log('✅ ReactiveStore initialized (TS)');
    }
}

// Create singleton instance
const SmartStore = new ReactiveStore();

// Phase 6.1: Register via direct import (using existing import at line 14)
if (!registry.get('Store')) {
    registry.register('Store', SmartStore);
    SmartStore.init();
}

// Export convenience wrappers
export const batch = (fn: () => void) => SmartStore.batch(fn);

export { SmartStore };
export default SmartStore;
